import React, { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { KButton, KDropdown, KInput, KSpan, KTooltip } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

import Loading from "../../../../ui/Loading"
import Pagination from "../../../../ui/table/Pagination"
import { assistant } from "../../../../../apis"
import { errorFilledToastOptions, LABEL_REQUEST_STATUS, successFilledToastOptions } from "../../../../../constants"
import { LabelRequest } from "../../../../../types"
import { mapListToCamelCase } from "../../../../../utility"

const COLUMNS = ["ID", "DATE", "ACCOUNT", "REQUESTED LABEL", "NOTE", "STATUS", ""]
// Same idea as the claim queue: fixed sizes for what has a fixed size, fr for the text that gives way.
const COLUMN_TEMPLATE = "44px 88px minmax(0, 1.4fr) minmax(0, 1.3fr) minmax(0, 1.5fr) 100px 220px"
const cellStyle = { minWidth: 0 }

// New first: the screen is a queue, and what is waiting is what it is opened for.
const statusOptions: KSelectOption[] = [
  { label: "New", value: 0, value2: LABEL_REQUEST_STATUS.NEW },
  { label: "Completed", value: 1, value2: LABEL_REQUEST_STATUS.COMPLETED },
  { label: "Rejected", value: 2, value2: LABEL_REQUEST_STATUS.REJECTED },
  { label: "All", value: 3, value2: "" }
]

const STATUS_LABELS: { [status: string]: string } = {
  [LABEL_REQUEST_STATUS.NEW]: "New",
  [LABEL_REQUEST_STATUS.COMPLETED]: "Completed",
  [LABEL_REQUEST_STATUS.REJECTED]: "Rejected"
}

// What the server answers a failed approval with, worded for whoever is working the queue.
const ERROR_MESSAGES: { [code: string]: string } = {
  not_open: "This request has already been handled.",
  label_not_created: "The label could not be created at Label Camp."
}

type AdminLabelRequestProps = {
  headerDiv: JSX.Element
  // Called after a request is closed, so the warning strip beside the menu counts again.
  onChange?: () => void
}

/**
 * Labels artists asked for through the Co-Manager.
 *
 * Approving one does what + New Label does, through the same call: the label is created at Label Camp
 * under the name settled on here. Assigning it to the artist stays in the user modal, like any other
 * label. The name can be corrected before it is created, the artist's own spelling is only a request.
 */
const AdminLabelRequest: FC<AdminLabelRequestProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [requests, setRequests] = useState<LabelRequest[]>([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [limit] = useState(10)
  const [selectedStatus, setSelectedStatus] = useState<KSelectOption>(statusOptions[0])
  const [search, setSearch] = useState("")

  const fetchRequests = (nextOffset: number, status?: string, name?: string) => {
    const statusParam = status ? `&status=${status}` : ""
    const searchParam = name?.trim() ? `&search=${encodeURIComponent(name.trim())}` : ""
    setLoading(true)
    assistant(`/label-requests/?offset=${nextOffset}&limit=${limit}${statusParam}${searchParam}`, "GET")
      .then((response) => {
        setRequests(mapListToCamelCase(response.data.results))
        setCount(response.data.count)
      })
      .catch(() => toast.error("The label requests could not be loaded.", errorFilledToastOptions))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchRequests(0, selectedStatus.value2, search)
  }, [])

  const applyFilters = () => {
    setOffset(0)
    fetchRequests(0, selectedStatus.value2, search)
  }

  const refresh = () => {
    fetchRequests(offset, selectedStatus.value2, search)
    props.onChange?.()
  }

  const showError = (error: any) => {
    const data = error?.response?.data || {}
    const message = ERROR_MESSAGES[data.error] || "The request could not be updated."
    toast.error(data.detail ? `${message} ${data.detail}` : message, errorFilledToastOptions)
  }

  const approve = (request: LabelRequest) => {
    const name = window.prompt("Label name to create at Label Camp:", request.name)
    if (name === null) return
    if (!name.trim()) {
      toast.error("The label name cannot be empty.", errorFilledToastOptions)
      return
    }
    const log = window.prompt("Enter log message for this action (optional):")
    if (log === null) return
    // Label Camp has no test environment and its labels cannot be deleted, so this is asked once more.
    if (!window.confirm(`Create the label "${name.trim()}" at Label Camp? Labels cannot be deleted.`)) return

    setLoading(true)
    // Not abortable: creating a label at Label Camp must not be cancelled by the list refreshing beside it.
    assistant(`/label-requests/${request.id}/approve/`, "POST", { name, log }, {}, false)
      .then(() => {
        toast.success("Label created. Assign it to the user from the user modal.", successFilledToastOptions)
        refresh()
      })
      .catch((error) => {
        setLoading(false)
        showError(error)
      })
  }

  const reject = (request: LabelRequest) => {
    const log = window.prompt("Why is this request rejected? (optional)")
    if (log === null) return

    setLoading(true)
    assistant(`/label-requests/${request.id}/reject/`, "POST", { log }, {}, false)
      .then(() => refresh())
      .catch((error) => {
        setLoading(false)
        showError(error)
      })
  }

  const statusColor = (request: LabelRequest) => {
    if (request.status === LABEL_REQUEST_STATUS.COMPLETED) return "#c3e6cb"
    if (request.status === LABEL_REQUEST_STATUS.REJECTED) return "#e2e3e5"
    return "#f5c6cb"
  }

  const renderActions = (request: LabelRequest) => {
    if (request.status !== LABEL_REQUEST_STATUS.NEW) {
      return (
        <KTooltip
          backgroundColor="black"
          position="bottom"
          content={<KSpan color="white" text={request.log || "-"} />}
          children={<KSpan text={request.createdName || "-"} fontSize={12} ellipsis />}
        />
      )
    }

    return (
      <div className="flex gap-1.5">
        <KButton
          dataTestId="admin-label-request-approve"
          width="120px"
          height="30px"
          fontSize={11}
          text="Approve"
          onClick={() => approve(request)}
          background="#000000da"
          textColor="white"
        />
        <KButton
          dataTestId="admin-label-request-reject"
          width="80px"
          height="30px"
          fontSize={11}
          text="Reject"
          onClick={() => reject(request)}
          background="#00000011"
          textColor="#666666"
        />
      </div>
    )
  }

  const renderRequests = () => (
    <div className="pt-4 pb-4" style={{ display: "flex", flexDirection: "column" }}>
      <div className="pb-2 gap-2 items-center" style={{ display: "grid", gridTemplateColumns: COLUMN_TEMPLATE }}>
        {COLUMNS.map((header) => (
          <div key={`header_${header}`} style={cellStyle}>
            <KSpan fontWeight={500} text={header} />
          </div>
        ))}
      </div>
      {requests.map((request) => (
        <div
          key={`label_request_${request.id}`}
          data-testid="admin-label-request-row"
          className="pt-2 pb-2 gap-2 items-center"
          style={{
            display: "grid",
            gridTemplateColumns: COLUMN_TEMPLATE,
            borderTop: "1px dashed grey",
            backgroundColor: statusColor(request)
          }}
        >
          <div style={cellStyle}>
            <KSpan text={String(request.id)} />
          </div>
          <div style={cellStyle}>
            <KSpan text={request.created?.substring(0, 10) || "-"} />
          </div>
          <div style={cellStyle} className="flex flex-col">
            <KSpan text={`${request.account || "-"} (${request.userId})`} ellipsis />
            <KSpan text={request.email || ""} fontSize={12} color="#999" ellipsis />
          </div>
          <div style={cellStyle}>
            <KSpan text={request.name} fontWeight={500} ellipsis />
          </div>
          <div style={cellStyle}>
            <KSpan text={request.note || "-"} fontSize={12} ellipsis />
          </div>
          <div style={cellStyle}>
            <KSpan text={STATUS_LABELS[request.status] || request.status} fontSize={12} />
          </div>
          <div style={cellStyle}>{renderActions(request)}</div>
        </div>
      ))}
      {!requests.length && !loading && (
        <div className="pt-4">
          <KSpan text="No label request here." color="#999" />
        </div>
      )}
    </div>
  )

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4 grow">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        {props.headerDiv}
        <div className="flex items-center gap-3">
          <div style={{ width: 180 }}>
            <KDropdown
              options={statusOptions}
              selected={selectedStatus}
              placeholder="Status"
              onSelect={(selected) => setSelectedStatus((selected as KSelectOption) || statusOptions[0])}
            />
          </div>
          <div style={{ width: 180 }}>
            <KInput value={search} placeholder="Search label" onChange={(value) => setSearch(value)} />
          </div>
          <KButton
            dataTestId="admin-label-request-apply-filters"
            height="35px"
            width="auto"
            padding="8px 16px"
            text="Apply Filters"
            background="black"
            textColor="white"
            onClick={applyFilters}
          />
        </div>
      </div>
      {renderRequests()}
      <div className="flex justify-center pb-6">
        <Pagination
          key=""
          hidden={count <= limit}
          size="sm"
          pageCount={Math.ceil(count / limit)}
          currentPage={offset / limit + 1}
          onClickPage={(pageNumber: number) => {
            setOffset((pageNumber - 1) * limit)
            fetchRequests((pageNumber - 1) * limit, selectedStatus.value2, search)
          }}
        />
      </div>
    </div>
  )
}

export default AdminLabelRequest
